import {
   ICadenceStep,
   ICampaignConfiguration,
   IContentReference,
   IContentPool,
   ICriteria,
   IPhoneDefinition,
   IStrategyDefinition,
   ISubtype,
} from '@forwardflow/features/campaign-v2/interfaces/campaign-v2.types'
import {
   IFilterRow,
   IFlowDraft,
   IPlaybook,
   TLeadType,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import type { ICampaignFilterConfig } from '@forwardflow/features/campaign-filter/interfaces'
import { hasFilterValue } from '@forwardflow/features/campaign-filter/utils/filter-values'
import { sortTouches } from '@forwardflow/features/campaign-flow/utils/playbook'
import { audienceTypes } from '@forwardflow/features/campaign-flow/utils/describe'
import { toTemplate } from '@forwardflow/features/campaign-flow/utils/text'

/**
 * Draft → backend shapes (campaign v2 handoff, sections 5 and 7).
 *
 * Pure functions with no network; covered by to-configuration.test.ts. The
 * live service calls these right before `createDraft` / `saveConfiguration`
 * and the content library endpoints.
 */

/** Business category ids come from `GET business-categories`; pass the real map in. */
export type TBusinessTypeIds = Record<TLeadType, number>

/** Content references returned by the library when the playbook is created or customized. */
export interface IPlaybookRefs {
   outboundScript: IContentReference
   strategy: IContentReference
   conversation: IContentReference
   /** Required to publish: how the assistant answers when a lead calls back. */
   inboundScript?: IContentReference | null
}

/** Keep saved scalar/null criteria intact; skip only incomplete value-based rows. */
export const filterToCriteria = (f: IFilterRow): ICriteria | null =>
   hasFilterValue(f)
      ? { field: f.field, operator: f.operator, value: f.value, logic: 'AND' }
      : null

/** The same audience in the existing campaign-filter shape (`ICampaignFilterConfig`), for the current backend. */
export const toFilterConfig = (draft: IFlowDraft): ICampaignFilterConfig => ({
   campaign_type: draft.audience.kinds,
   lead_type_filter: draft.audience.leadTypes,
   /* The dashboard's activity triggers, when the campaign reacts to activity. */
   activity_campaign_filter_criteria:
      draft.audience.kinds.includes('activity') && draft.audience.activity
         ? {
              events: draft.audience.activity.events.length
                 ? draft.audience.activity.events
                 : ['all'],
              lookback_days: draft.audience.activity.lookbackDays,
              triggers: draft.audience.activity.pondIds.map((id) => ({
                 type: 'pond' as const,
                 value: Number(id),
              })),
           }
         : null,
   campaign_filters: {
      eligible_criteria: draft.audience.filters.filter(hasFilterValue),
   },
   first_to_contact: { buffer_time: 0 },
})

export const toCadence = (pb: Pick<IPlaybook, 'touches'>): ICadenceStep[] =>
   sortTouches(pb.touches).map((t, i) => ({
      id: t.id,
      order: i + 1,
      day: t.day,
      localTime: t.time,
      action: t.action === 'call' ? 'call' : 'sms',
      ...(t.action === 'call'
         ? {
              voicemail: t.voicemail
                 ? ('if-configured' as const)
                 : ('none' as const),
           }
         : {}),
   }))

/** Texts and voicemails as one outbound content group (a "strategy" to the backend). Names are generated. */
export const toStrategyDefinition = (
   pb: IPlaybook,
   businessTypeId: number,
   campaignName: string
): IStrategyDefinition => ({
   name: `${campaignName} - Outbound content group 1`,
   businessTypeId,
   sms: pb.texts.map((m) => ({ template: toTemplate(m.text) })),
   email: [],
   voicemail: pb.voicemails.map((m) => ({ template: toTemplate(m.text) })),
})

/** The call script as an outbound phone definition; the first section's text is the opener. */
export const toPhoneDefinition = (
   pb: IPlaybook,
   businessTypeId: number,
   campaignName: string
): IPhoneDefinition => ({
   name: `${campaignName} - Call script`,
   businessTypeId,
   callType: 'outbound',
   callDirections: ['outbound'],
   leadTypes: ['known'],
   callerTypes: ['ai'],
   firstMessage: toTemplate(pb.opening ?? ''),
   /*
    * A copy carries no section ids or locks, and no empty section: the
    * backend answers "Invalid content edit" to either (seen on production).
    */
   sections: pb.script
      .filter((s) => s.text.trim())
      .map((s, i) => ({
         name: s.title,
         type: s.type ?? 'call_outbound',
         order: i + 1,
         content: s.protected ? s.text : toTemplate(s.text),
         ...(s.templateType ? { templateType: s.templateType } : {}),
      })),
})

/** How the engine tells the lead types apart when a campaign has several. */
const SUBTYPE: Record<TLeadType, { name: string; description: string }> = {
   buyer: {
      name: 'Buyer',
      description:
         'The lead wants to buy a home: they asked about a listing, saved homes, requested a showing, or said they are looking to purchase.',
   },
   seller: {
      name: 'Seller',
      description:
         'The lead wants to sell a home: they asked what their home is worth, requested a cash offer, or said they are thinking of listing.',
   },
   rental: {
      name: 'Rental',
      description:
         'The lead wants to rent: they asked about a rental listing, lease terms, or said they are looking for a place to rent.',
   },
}

/** Content references per lead type; a type without its own uses the first type's. */
export type TPlaybookRefsByType = Partial<Record<TLeadType, IPlaybookRefs>>

export const toConfiguration = (
   draft: IFlowDraft,
   ids: TBusinessTypeIds,
   refs: TPlaybookRefsByType | null
): ICampaignConfiguration => {
   const types = audienceTypes(draft.audience)
   const businessTypeIds = types.map((t) => ids[t])
   /*
    * One content pool per lead type, each with its own script, outbound content group
    * and reply instructions. The first lead type's pool is the default.
    */
   const poolId = (t: TLeadType, i: number) =>
      i === 0 ? 'pool-default' : `pool-${t}`
   /* Each lead type has exactly one pool, so each is its type's default. */
   const pool = (t: TLeadType, i: number): IContentPool => {
      const r = refs?.[t] ?? refs?.[types[0]] ?? null
      return {
         id: poolId(t, i),
         businessTypeId: ids[t],
         isDefault: true,
         outboundScripts: r ? [r.outboundScript] : [],
         inboundScripts: r?.inboundScript ? [r.inboundScript] : [],
         strategies: r ? [r.strategy] : [],
         conversationContent: r ? [r.conversation] : [],
      }
   }
   /*
    * A campaign with several lead types must tell the engine which words a
    * lead gets. Almost no lead carries an interest type in Follow Up Boss, so
    * the engine classifies each lead from a description instead of a field
    * rule (hand-off section 8: empty conditions = description-based).
    */
   const subtype = (t: TLeadType, i: number): ISubtype => ({
      id: `subtype-${t}`,
      name: SUBTYPE[t].name,
      description: SUBTYPE[t].description,
      poolId: poolId(t, i),
      priority: (i + 1) * 10,
      conditions: [],
   })
   const criteria = draft.audience.filters
      .map(filterToCriteria)
      .filter((c): c is ICriteria => !!c)
   return {
      schemaVersion: 2,
      businessTypeIds,
      source: { mode: 'dynamic', criteria },
      routing: {
         enabled: true,
         strategy:
            draft.handoff.mode === 'first_to_answer'
               ? 'jump_ball'
               : 'round_robin',
         agentIds: draft.handoff.agentIds,
      },
      phoneNumbers: draft.numbers.selected.map((n) => toE164(n.number)),
      contentPools: types.map(pool),
      subtypes: types.length > 1 ? types.map(subtype) : [],
      /* Callers the engine has not classified hear the first lead type's words. */
      ...(types.length > 1 ? { incomingCallPoolId: poolId(types[0], 0) } : {}),
      cadence: toCadence(draft.playbook),
      selectionPolicy: {
         phone: 'round_robin',
         strategy: 'ordered_journey_v1',
         exhaustion: 'repeat-last',
      },
   }
}

/** "(719) 555-0140" → "+17195550140" (US numbers). */
export const toE164 = (display: string) => {
   const digits = display.replace(/\D/g, '')
   return digits.length === 10 ? `+1${digits}` : `+${digits}`
}
